import React, { useState, useEffect, useCallback } from 'react'
import { X, Copy, Check } from 'lucide-react'
import { userKanriService } from '../../services/userKanriService'
import { useApiError } from '../../../shared/hooks/useApiError'
import MessageDialog from '../../../shared/ui/MessageDialog'
import Button from '../../../shared/ui/Button'

const PixClientModal = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)
  const [hasToken, setHasToken] = useState(null) // null = checking, true = has token, false = no token
  const [pixClientToken, setPixClientToken] = useState(null)
  const [copied, setCopied] = useState(false)

  const {
    showErrorDialog,
    errorMessage,
    messageType,
    handleApiError,
    handleApiResponse,
    closeErrorDialog,
    showSuccessMessage
  } = useApiError()

  const checkTokenStatus = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await userKanriService.getPixClientToken()
      
      // Check for business logic errors in the response
      if (response.returnCode !== '200') {
        setHasToken(false)
        setPixClientToken(null)
        return
      } else {
        setHasToken(true)
        // Store the token if available
        if (response.pixClientToken) {
          setPixClientToken(response.pixClientToken)
        }
      }
    } catch (error) {
      handleApiError(error, 'トークンの確認に失敗しました')
      setHasToken(false)
      setPixClientToken(null)
    } finally {
      setIsLoading(false)
    }
  }, [handleApiError])

  // Check token status when modal opens
  useEffect(() => {
    if (isOpen) {
      checkTokenStatus()
    } else {
      setHasToken(null)
      setPixClientToken(null)
    }
  }, [isOpen, checkTokenStatus])

  const handleRegister = async () => {
    try {
      setIsRegistering(true)
      
      // JWT token contains user information, so no need to load user data first
      const response = await userKanriService.addPixClientToken({})

      if (handleApiResponse(response, 'UUIDの登録に失敗しました')) {
        return
      }

      if (response.returnCode === '200') {
        showSuccessMessage('UUIDの登録が完了しました')
        setHasToken(true)
        // Store the token if available
        if (response.pixClientToken) {
          setPixClientToken(response.pixClientToken)
        }
      }
    } catch (error) {
      handleApiError(error, 'UUIDの登録に失敗しました')
    } finally {
      setIsRegistering(false)
    }
  }

  const handleOpenDesktopApp = () => {
    try {
      // Open desktop app using pixclient:// protocol
      window.location.href = 'pixclient://upload/'
    } catch (error) {
      handleApiError(error, 'デスクトップアプリの起動に失敗しました')
    }
  }

  const handleCopyToken = async () => {
    if (!pixClientToken) return

    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(pixClientToken)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = pixClientToken
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch (error) {
      handleApiError(error, 'コピーに失敗しました')
    }
  }

  const handleClose = () => {
    setHasToken(null)
    setPixClientToken(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="pixclient-modal">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">PIX Client</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="close-modal-button"
            >
              <X size={20} />
            </button>
          </div>

          <div className="mb-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">確認中...</span>
              </div>
            ) : hasToken === false ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  PIX Clientトークンが登録されていません。
                  <br />
                  デスクトップアプリを使用するには、まずトークンを登録してください。
                </p>
                <Button
                  onClick={handleRegister}
                  disabled={isRegistering}
                  className="w-full"
                  data-testid="register-token-button"
                >
                  {isRegistering ? '登録中...' : 'UUID登録'}
                </Button>
              </div>
            ) : hasToken === true ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  PIX Clientトークンが登録されています。
                </p>
                {pixClientToken && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      PIX Clientトークン
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={pixClientToken}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                        data-testid="pixclient-token-input"
                      />
                      <button
                        onClick={handleCopyToken}
                        className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                        title="トークンをコピー"
                        aria-label="トークンをコピー"
                        data-testid="copy-token-button"
                      >
                        {copied ? (
                          <Check size={18} className="text-green-600" />
                        ) : (
                          <Copy size={18} className="text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                <Button
                  onClick={handleOpenDesktopApp}
                  className="w-full"
                  data-testid="open-desktop-app-button"
                >
                  デスクトップアプリ設定
                </Button>
                <div className="mt-6 space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-base font-semibold text-blue-900 mb-3">
                      動作フロー
                    </h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 ml-2">
                      <li>PIX Clientがアップロードモードで起動</li>
                      <li>ログイン画面で会社名・ユーザー名・パスワードを入力</li>
                      <li>認証成功後、設定フォルダ内の画像を自動的にアップロード</li>
                      <li>アップロード進捗と結果を表示</li>
                    </ol>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="text-base font-semibold text-amber-900 mb-3">
                      前提条件
                    </h4>
                    <ul className="list-disc list-inside space-y-2 text-sm text-gray-700 ml-2">
                      <li>PIX Clientの設定でアップロードフォルダが設定されていること</li>
                      <li>アップロードフォルダに画像ファイル（jpg, png, gif等）が存在すること</li>
                      <li>有効なログイン情報を持っていること</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-end">
            <Button
              onClick={handleClose}
              variant="outline"
              className="px-4 py-2"
              data-testid="cancel-button"
            >
              閉じる
            </Button>
          </div>
        </div>
      </div>

      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title={
          messageType === 'error' ? 'エラー' :
            messageType === 'success' ? '成功' :
              messageType === 'warning' ? '警告' :
                '情報'
        }
        message={errorMessage}
        type={messageType}
        buttonText="閉じる"
        data-testid="message-dialog"
      />
    </>
  )
}

export default PixClientModal

